
import { Link, useNavigate } from "react-router-dom";
import { getAllIndustries } from "../data/industriesData";


const IndustryPage = () => {
  const navigate = useNavigate();
  const industries = getAllIndustries();

  // URL Slug generator
  const handleCardClick = (industry) => {
    const industrySlug = industry.name.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/&/g, '')
      .replace(/--+/g, '-');
    
    navigate(`/industry/${industrySlug}`);
  };

  return (
    <div className="pt-24 min-h-screen bg-white relative selection:bg-[#952877]/10 selection:text-[#952877]">
      {/* Decorative Background Elements - Premium Light Theme */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#952877]/5 blur-[120px] rounded-full pointer-events-none"></div>
      <div className="absolute bottom-1/4 left-0 w-[400px] h-[400px] bg-[#592aa7]/5 blur-[100px] rounded-full pointer-events-none"></div>

      {/* --- HERO SECTION --- */}
      <section className="py-20 text-center relative z-10">
        <div className="max-w-5xl mx-auto px-6">

          <h1 className="text-5xl md:text-8xl font-black text-[#17091a] mb-8 tracking-tighter leading-[1.1]">
            Industries We <span className="bg-gradient-to-r from-[#952877] via-[#592aa7] to-[#17091a] bg-clip-text text-transparent ">Revolutionize</span>
          </h1>
          <p className="text-xl text-gray-500 max-w-3xl mx-auto font-light leading-relaxed">
            Discover how we're architecting cutting-edge AI ecosystems tailored to unique global challenges.
          </p>
        </div>
      </section>

      {/* --- INDUSTRIES GRID --- */}
      <section className="pb-32 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <div
                key={industry.id}
                onClick={() => handleCardClick(industry)}
                className="group cursor-pointer"
                style={{
                  // Native CSS animation to replace GSAP stagger on load
                  animation: `fadeInUp 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) ${index * 0.1}s both`
                }}
              >
                {/* Custom Card Wrapper to ensure Windows Scaling doesn't break alignment */}
                <div className="bg-white rounded-[40px] border border-gray-100 p-8 h-full transition-all duration-500 hover:shadow-[0_40px_80px_-20px_rgba(23,9,26,0.08)] hover:-translate-y-3 relative overflow-hidden">
                  
                  {/* Subtle Brand Accent on Hover */}
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#952877] to-[#592aa7] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>

                  <div className="mb-10 bg-gray-50 group-hover:bg-[#17091a] w-20 h-20 rounded-[24px] flex items-center justify-center transition-all duration-500 shadow-inner group-hover:shadow-xl group-hover:rotate-6">
                    <img 
                      src={industry.image} 
                      alt={industry.name} 
                      className="w-10 h-10 grayscale group-hover:grayscale-0 group-hover:invert transition-all duration-500" 
                    />
                  </div>

                  <h3 className="text-2xl font-black text-[#17091a] mb-4 group-hover:text-[#952877] transition-colors tracking-tight">
                    {industry.name}
                  </h3>
                  
                  <p className="text-gray-500 font-medium text-sm leading-relaxed mb-10 line-clamp-3">
                    {industry.description || "Deploying neural networks and automated logic to optimize " + industry.name + " workflows."}
                  </p>

                  <div className="flex items-center text-[#17091a] font-black text-xs uppercase tracking-widest border-t border-gray-50 pt-8 group-hover:text-[#952877]">
                    Explore Sector
                    <svg className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

{/* --- CTA SECTION --- */}
      <section className="py-32 bg-white relative overflow-hidden">
        {/* Background Accent Glows */}
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#952877]/10 blur-[120px] rounded-full pointer-events-none"></div>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#592aa7]/5 blur-[120px] rounded-full pointer-events-none"></div>
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          {/* Liquid Glass Container */}
          <div className="relative bg-white/40 backdrop-blur-2xl border border-white/60 rounded-[4rem] p-12 md:p-24 text-center shadow-[0_32px_64px_-16px_rgba(0,0,0,0.05),inset_0_0_0_1px_rgba(255,255,255,0.7)] overflow-visible">
            
      
            <div className="absolute inset-0 rounded-[4rem] bg-gradient-to-br from-white/40 to-transparent pointer-events-none"></div>

            <div className="relative z-10">
              <div className="inline-block px-4 py-1.5 mb-10 rounded-full border border-[#952877]/20 bg-[#952877]/5">
                 <span className="text-[10px] font-black tracking-[0.2em] uppercase text-[#952877]">Ready to Scale?</span>
              </div>

              
              <h2 className="text-4xl md:text-6xl lg:text-7xl font-black text-[#17091a] mb-10 tracking-normal leading-[1.2] md:leading-[1.15]">
                Architecting for <br className="hidden md:block" />
                <span className="bg-gradient-to-r from-[#952877] via-[#592aa7] to-[#17091a] bg-clip-text text-transparent italic px-1">
                  New Frontiers?
                </span>
              </h2>
              
              <p className="text-lg md:text-xl text-gray-600 mb-14 font-medium max-w-2xl mx-auto leading-relaxed">
                We work across diverse sectors. Let's discuss how ViconAI can engineer a bespoke solution for your specific industry challenge.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
                <Link 
                  to="/contact"
                  className="group relative inline-flex items-center px-12 py-6 bg-[#17091a] text-white font-black text-xs uppercase tracking-[0.2em] rounded-2xl transition-all duration-500 hover:shadow-[0_20px_40px_rgba(23,9,26,0.3)] hover:-translate-y-1 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[#952877] to-[#592aa7] translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
                  <span className="relative z-10 flex items-center">
                    Consult Our Experts
                    <svg className="w-4 h-4 ml-3 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </span>
                </Link>
                
                <Link 
                  to="/services" 
                  className="group text-[#17091a] font-black text-xs uppercase tracking-[0.2em] flex items-center gap-3"
                >
                  <span className="group-hover:text-[#952877] transition-colors">Explore Services</span>
                  <div className="w-8 h-[2px] bg-[#17091a] group-hover:bg-[#952877] group-hover:w-12 transition-all"></div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Native CSS Animations for Windows Stability */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}} />
    </div>
  );
};

export default IndustryPage;